"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.callback = exports.observable = exports.subject = void 0;
const error_1 = require("./error");
const lib_1 = require("./lib");
/**
 * Class decorator to make instances of the decorated class a subject.
 *
 * ```ts
 *    ＠subject()
 *    class Foo {}
 * ```
 *
 * Enables {@link observable | @observable()} and {@link callback | @callback}
 * decorators on members of the class.
 *
 * Observers can be registered directly on the subject to be notified when any
 * `@observable` property is assigned:
 * ```ts
 *    const foo = new Foo();
 *    observe(foo, () => { ... });
 * ```
 *
 * @see {@link observable | @observable()}
 * @see {@link callback | @callback}
 *
 */
const subject = () => (target) => {
    const subjectDef = lib_1.ClassSubjectDef.getOrCreate(target.prototype);
    subjectDef.decorated();
    return new Proxy(target, {
        construct: (target, argArray) => {
            const instance = new target.prototype.constructor(...argArray);
            subjectDef.apply(instance);
            return instance;
        },
    });
};
exports.subject = subject;
/**
 * Property decorator which makes a property of a subject observable. Can be
 * used on both simple properties & getter / setter pairs.
 *
 * ```ts
 *    ＠subject()
 *    class Foo {
 *      ＠observable()
 *      bar = 0;
 *
 *      #baz = 0
 *      ＠observable()
 *      get baz() { return this.#baz; }
 *      private set baz(value) { this.#baz = value; }
 *    }
 * ```
 *
 * Observers can be registered to be notified whenever the property is assigned:
 * ```ts
 *    const foo = new Foo();
 *    observe(foo, 'bar', () => { ... })
 *    observe(foo, 'baz', () => { ... })
  * ```
 *
 * Requires that the containing class is decorated with the
 * {@link subject | @subject()} decorator.
 */
const observable = () => (target, key, descriptor) => {
    /*
        If we are passed a function, it is the class constructor, and we are dealing
        with a static member.

        If passed an object, it is the class' prototype, and we are dealing with
        an instance member.

        https://www.typescriptlang.org/docs/handbook/decorators.html#property-decorators
    */
    if (typeof (target) === 'function') {
        throw new error_1.EcceError(`Static @observable() members are not supported: "${target.name}.${String(key)}"`);
    }
    lib_1.ClassSubjectDef.getOrCreate(target).addProperty(key, descriptor !== null && descriptor !== void 0 ? descriptor : null);
};
exports.observable = observable;
/**
 * Method decorator which binds a method to the instance, allowing it to be
 * passed around directly as a callback.
 *
 * ```ts
 *    ＠subject()
 *    class Foo {
 *       foo = 0;
 *
 *       ＠callback
 *       bar() {
 *         console.log(this.foo)
 *       }
 *    }
 *
 *    const foo = new Foo();
 *    document.getElementById('button').onclick = foo.bar;
 * ```
 *
 * Requires that the containing class is decorated with the
 * {@link subject | @subject()} decorator.
 */
const callback = (target, key) => {
    lib_1.ClassSubjectDef.getOrCreate(target).addCallback(key);
};
exports.callback = callback;
//# sourceMappingURL=decorators.js.map